/**
 * Decline copy — the ONE place every "the planner said something about this field" string lives
 * (s178, Block A3; extended per-reason s179, TWO-REASON-DECLINE-1).
 *
 * A declined field is an ANSWER, not a blank and not a failure (D-177.17c). Before this module
 * the product told that answer four different ways, in four files, and the planner-facing one —
 * a muted italic "Not available" — read like an outage rather than something they had said.
 *
 * 🔴 WHY THE WORDINGS DIFFER, DELIBERATELY. Three audiences read a decline and they need
 * different sentences:
 *   • the PLANNER is being reminded of their own answer            → second person
 *   • the EXECUTOR reading the snapshot needs provenance           → "declined by planner"
 *   • the EXECUTOR filling a form needs to know why a cell is empty → "Planner indicated: …"
 *
 * 🔴 WHY A MODULE AND NOT LITERALS. Derived-not-duplicated: the reason there were four strings is
 * that each surface invented its own, and nothing stopped a fifth. A surface that needs decline
 * copy IMPORTS it and picks an audience + density; it cannot mint a new sentence without editing
 * this file, which is the point. s179 multiplies the matrix by three reasons — 3 × 2 audiences ×
 * 2 densities — which is exactly the growth that would have gone feral as inline ternaries.
 *
 * Density (planner only):
 *   • COMPACT  — the expandable ribbon cell, and any future table cell: a truncating,
 *     text-xs slot with no room for a sentence.
 *   • EXPANDED — the drawer, the card, the PYA census layout: full-width, room to speak.
 * The choice is a property of the SURFACE, never a per-site judgement call.
 *
 * 🔴 s179 — WHAT EACH REASON SAYS, and why one boolean could not carry it:
 *   • `dont_have`          the fact does not exist. The executor STOPS LOOKING. Wording UNCHANGED
 *                          from s178 for both executor audiences (D-178.7: audience-correct
 *                          already, and rewording an executor artifact was never the defect).
 *   • `wont_store`         it exists; the planner declined to record it. The executor OBTAINS IT
 *                          at the institution.
 *   • `giving_another_way` it exists and is being handed over outside the app. The executor CHECKS
 *                          THE ESTATE DOCUMENTS first.
 *
 * 🔴 The executor notes state ONLY WHAT IS KNOWN. We know a `wont_store` fact exists and where it
 * lives; we do not know the account number, and we never imply the executor can find it here.
 */
import { type DeclineReason } from "./decline-reason";

/** Planner-facing density for a decline status line. */
export type DeclineDensity = "compact" | "expanded";

/**
 * 🔴 The reason-ABSENT case. `generic_entries.decline_reason` is nullable, so every renderer must
 * be total — but this is NOT a legacy-compatibility path. The s179 build CLEARED every reasonless
 * decline on the nine in-scope fields (F&F concluded, D-177.6b), and the reason picker always
 * sends one. A null reads as the most conservative honest statement we can make, which is the
 * `dont_have` wording: it claims the least about a fact we know nothing about.
 */
const FALLBACK: DeclineReason = "dont_have";

const resolve = (r: DeclineReason | null | undefined): DeclineReason => r ?? FALLBACK;


// ── Planner, compact slot (expandable ribbon; a table cell if one ever renders a decline) ────────
// 🔴 These share the ribbon's truncating `text-xs` cell, so they are TOKENS, not sentences. The
// em-dash prefix is the existing visual convention that marks the cell as answered-not-empty.
export const DECLINE_PLANNER_COMPACT: Record<DeclineReason, string> = {
  dont_have: "— none",
  wont_store: "— withheld",
};

// ── Planner, full-width surface (drawer, card, Protect Your Access) ──────────────────────────────
// Second person, because the planner is being reminded of their own answer.
export const DECLINE_PLANNER_EXPANDED: Record<DeclineReason, string> = {
  dont_have: "You said you don't have this",
  wont_store: "You chose not to store this here",
};

// ── Executor, full-plan snapshot / handoff PDF (D-126.G) ─────────────────────────────────────────
// The executor needs PROVENANCE, not the planner's voice. `dont_have` is verbatim s178.
export const DECLINE_EXECUTOR_SNAPSHOT: Record<DeclineReason, string> = {
  dont_have: "Not provided (declined by planner)",
  wont_store: "Not stored here (planner withheld this)",
};

// ── Executor, form-gap prompt (FORM-GAP-DECLINE-CONTEXT-1, D-155) ────────────────────────────────
// This explains an EMPTY FORM CELL, which is a different question from "what did the planner say".
// `dont_have` is verbatim s178.
export const DECLINE_FORM_GAP: Record<DeclineReason, string> = {
  dont_have: "Planner indicated: not available",
  wont_store: "Planner indicated: not stored here",
};

/**
 * The executor's NEXT ACTION when the planner left NO note — the fallback instruction.
 *
 * 🔴 `dont_have` is deliberately `null`, not an empty string and not a reassurance. The fact does
 * not exist; telling the executor to do anything about it would be inventing work. This null is
 * the difference between the two classes the whole build is about.
 */
export const DECLINE_EXECUTOR_ACTION: Record<DeclineReason, string | null> = {
  dont_have: null,
  wont_store: "Request it from the institution.",
};

/**
 * The planner's undo affordance — 🔴 PER REASON since s179 (G4 finding 1).
 *
 * It used to be ONE string, "I have this now", on the reasoning that undo is undo whatever the
 * reason was. That is true of the ACTION and false of the SENTENCE. "I have this now" is coherent
 * only for `dont_have`: said to a planner who withheld an account number they are holding in their
 * hand, it contradicts what they just told us, and it was one of the two strings the G4 pass caught
 * reading as `dont_have` on a `wont_store` decline.
 */
export const DECLINE_UNDO_LABEL: Record<DeclineReason, string> = {
  dont_have: "I have this now",
  wont_store: "I'll store it here after all",
};

/**
 * 🔴 THE TWO BUTTONS (s179 rebuild) — the affordance itself, not a menu that opens one.
 *
 * These render side by side directly under an empty eligible field. There is no trigger link and
 * no popover: the earlier cut put a neutral trigger in front of a three-option picker, which meant
 * a planner had to click something before learning what was on offer, and the trigger inevitably
 * either asserted one of the answers ("I don't have this" — G4 finding 2) or said nothing at all.
 * Showing both answers at once removes the problem rather than rewording it.
 *
 * First person, because the planner is making a statement about their own situation.
 */
export const DECLINE_REASON_OPTION_LABEL: Record<DeclineReason, string> = {
  dont_have: "I don't have this information",
  wont_store: "I'd rather not store this here",
};

// ── The follow-up, shown only after `wont_store` ─────────────────────────────────────────────────

/** The follow-up question. Optional to answer — "no" is a complete response. */
export const DECLINE_NOTE_PROMPT = "Are you providing this another way?";

/** The location field's label, asked only once the planner says yes. */
export const DECLINE_NOTE_LABEL = "Where will your executor find it?";

/**
 * 🔴 PLACEHOLDER + HELPER — THE ONLY THING STANDING BETWEEN THIS FIELD AND A STORED SECRET.
 *
 * A planner asked "where will your executor find it?" may reasonably type the account number
 * instead of the location — the question is about the thing, so the thing comes to mind. We cannot
 * validate content (any string is a plausible location), so the WORDING carries the whole weight.
 *
 * The placeholder models the answer rather than describing it: a planner who sees a location in the
 * box writes a location. The helper says the quiet part out loud, in the planner's interest rather
 * than as a rule imposed on them — "this stays in your plan" is the reason it matters to THEM.
 *
 * 🔴 COPY IS READE'S CALL. Flagged for ruling at review.
 */
export const DECLINE_NOTE_PLACEHOLDER = "e.g. in the envelope in my safe deposit box";
export const DECLINE_NOTE_HELPER =
  "Just where to look — not the number or password itself. This note is stored in your plan.";

/** The follow-up's two answers. "No" is a complete answer and leaves the note empty. */
export const DECLINE_NOTE_YES = "Yes — I'll say where";
export const DECLINE_NOTE_NO = "No";

/** Saves the note and closes the follow-up. */
export const DECLINE_NOTE_SAVE = "Save";

// ── Resolvers — every surface calls one of these rather than indexing a map itself ───────────────

/** The planner-facing decline string for a surface's density and the field's reason. */
export function plannerDeclineText(density: DeclineDensity, reason?: DeclineReason | null): string {
  const r = resolve(reason);
  return density === "compact" ? DECLINE_PLANNER_COMPACT[r] : DECLINE_PLANNER_EXPANDED[r];
}

/** The undo label phrased for the answer being withdrawn (s179). */
export function plannerUndoLabel(reason?: DeclineReason | null): string {
  return DECLINE_UNDO_LABEL[resolve(reason)];
}

/** The executor snapshot / handoff-PDF string. */
export function executorSnapshotDeclineText(reason?: DeclineReason | null): string {
  return DECLINE_EXECUTOR_SNAPSHOT[resolve(reason)];
}

/** The executor form-gap string. */
export function executorFormGapDeclineText(reason?: DeclineReason | null): string {
  return DECLINE_FORM_GAP[resolve(reason)];
}

/**
 * What the executor should DO — 🔴 the planner's own words first, when they left any.
 *
 * "In the envelope in my safe deposit box" is a better instruction than anything we can generate
 * from a category, and capturing it is the entire point of the note. The generated fallback is what
 * we say when we have nothing better; it must never displace what the planner actually wrote.
 *
 * Returns `null` only for `dont_have`, where there is genuinely nothing to chase.
 */
export function executorDeclineAction(
  reason?: DeclineReason | null,
  note?: string | null,
): string | null {
  const r = resolve(reason);
  if (DECLINE_EXECUTOR_ACTION[r] === null) return null; // dont_have — no action exists
  const trimmed = (note ?? "").trim();
  return trimmed !== "" ? `The planner noted: ${trimmed}` : DECLINE_EXECUTOR_ACTION[r];
}
